import { useState, useEffect, useRef } from "react";
import { api, bannerImageUrl } from "@/lib/appresse";

// A fixed home-page ad placement that rotates its creatives (images/videos) as a
// small left-right carousel. Falls back to `fallback` (the legacy hardcoded ad)
// when the admin hasn't configured this slot yet.
export const SlotAd = ({ slot, className = "", fallback = null }) => {
  const [items, setItems] = useState(null);
  const [i, setI] = useState(0);
  const seen = useRef(new Set());

  useEffect(() => {
    let alive = true;
    const load = () => api.publicBannerSlots()
      .then((d) => { if (alive) setItems(d?.[slot] || []); })
      .catch(() => { if (alive && items === null) setItems([]); });
    load();
    // Keep the ad fresh: refetch periodically and whenever the tab regains focus,
    // so a newly published creative replaces the old one without a manual reload.
    const iv = setInterval(load, 60000);
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot]);

  const list = items || [];
  const count = list.length;

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % count), 5500);
    return () => clearInterval(t);
  }, [count]);

  const cur = list[i] || list[0];
  useEffect(() => {
    if (!cur?.id || seen.current.has(cur.id)) return;
    seen.current.add(cur.id);
    api.bannerEvent(cur.id, "impression").catch(() => {});
  }, [cur]);

  if (items === null) return null;
  if (count === 0) return fallback;

  const isVideo = cur.media_type === "video";
  const media = isVideo ? (
    <video src={`${bannerImageUrl(cur.id)}?v=${cur.media_v || ""}`} autoPlay loop muted playsInline
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
  ) : (
    <img src={`${bannerImageUrl(cur.id)}?v=${cur.media_v || ""}`} alt={cur.title || "Publicité"} loading="lazy"
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
  );
  const dots = count > 1 && (
    <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
      {list.map((_, idx) => (
        <button key={idx} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setI(idx); }} aria-label={`Pub ${idx + 1}`}
          className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />
      ))}
    </div>
  );
  const boxClass = "relative overflow-hidden rounded-3xl border border-border shadow-[0_10px_28px_-6px_rgba(0,0,0,0.4)] transition-shadow duration-300 group-hover:shadow-[0_16px_40px_-8px_rgba(0,0,0,0.55)] aspect-[3/1]";

  return (
    <div className={`block group ${className}`} data-testid={`slot-ad-${slot}`}>
      {cur.link_url ? (
        <a href={cur.link_url} target="_blank" rel="noopener noreferrer sponsored"
          onClick={() => api.bannerEvent(cur.id, "click").catch(() => {})}
          className={`block cursor-pointer ${boxClass}`}>
          {media}
          {dots}
        </a>
      ) : (
        <div className={boxClass}>{media}{dots}</div>
      )}
    </div>
  );
};
